<template>
  <div class="card mb-25 border-0 rounded-0 bg-white">
    <div class="card-body p-15 p-sm-20 p-lg-25 letter-spacing">
      <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-20">
        <h5 class="card-title fw-bold mb-0">{{ $t("header.notifications") }}</h5>
        <div class="d-flex align-items-center gap-3">
          <div class="form-check form-switch mb-0">
            <input id="onlyUnread" v-model="onlyUnread" class="form-check-input" type="checkbox" role="switch" @change="load(true)" />
            <label class="form-check-label" for="onlyUnread">{{ $t("header.onlyUnread") }}</label>
          </div>
          <button type="button" class="btn btn-outline-primary btn-sm" @click="markAllRead">
            {{ $t("header.markAllRead") }}
          </button>
        </div>
      </div>

      <p v-if="!items.length && !loading" class="text-muted text-center py-4 mb-0">{{ $t("header.noNotifications") }}</p>

      <div class="list-group list-group-flush">
        <button
          v-for="n in items"
          :key="n.id"
          type="button"
          :class="['list-group-item list-group-item-action text-start notif-row', { 'notif-row--unread': !n.read_at }]"
          @click="open(n)"
        >
          <span class="d-block fw-semibold">{{ n.title }}</span>
          <span v-if="n.body" class="d-block text-muted fs-14">{{ n.body }}</span>
          <span class="d-block text-muted fs-13 mt-1">{{ formatDate(n.created_at) }}</span>
        </button>
      </div>

      <div class="text-center mt-3">
        <span v-if="loading" class="spinner-border spinner-border-sm"></span>
        <button v-else-if="hasMore" type="button" class="btn btn-light" @click="load(false)">
          {{ $t("header.loadMore") }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import api from "@/utils/axios";
import { dateLocale } from "@/i18n";

interface AlertRow {
  id: number;
  title: string;
  body?: string | null;
  link?: string | null;
  read_at?: string | null;
  created_at: string;
}

const PER_PAGE = 20;

export default defineComponent({
  name: "NotificationsContent",
  setup() {
    const router = useRouter();
    const items = ref<AlertRow[]>([]);
    const page = ref(0);
    const hasMore = ref(false);
    const loading = ref(false);
    const onlyUnread = ref(false);

    // page-by-page: nothing is loaded until the user asks for the next page
    async function load(reset: boolean) {
      if (loading.value) return;
      if (reset) {
        page.value = 0;
        items.value = [];
      }
      loading.value = true;
      try {
        const res = await api.get("/api/auth/user-alerts/all", {
          params: { page: page.value + 1, per_page: PER_PAGE, unread: onlyUnread.value ? 1 : 0 },
        });
        items.value.push(...(res.data.data ?? []));
        hasMore.value = !!res.data.next_page_url;
        page.value += 1;
      } finally {
        loading.value = false;
      }
    }

    async function open(n: AlertRow) {
      if (!n.read_at) {
        n.read_at = new Date().toISOString();
        api.post(`/api/auth/user-alerts/${n.id}/read`).catch(() => {
          n.read_at = null; // request failed: keep it unread
        });
      }
      if (n.link) router.push(n.link);
    }

    async function markAllRead() {
      await api.post("/api/auth/user-alerts/read-all");
      load(true);
    }

    function formatDate(value: string) {
      return new Date(value).toLocaleString(dateLocale(), {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }

    onMounted(() => load(true));

    return { items, hasMore, loading, onlyUnread, load, open, markAllRead, formatDate };
  },
});
</script>

<style scoped>
.notif-row { border-inline-start: 3px solid transparent; }
.notif-row--unread { border-inline-start-color: var(--brand-accent, #ff5500); background: rgba(var(--brand-accent-rgb, 255, 85, 0), 0.05); }
</style>
